import React, { useState, useEffect, useRef } from 'react';
import Highcharts, { SeriesOptionsType, merge } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { DataLoadingProps } from 'components/features/home/assetStuffing/AssetStuffing';
import { commaFormat } from 'components/features/new-report/utils/formatter';
// import { marker } from 'components/features/chartUtils';
import { arrangeDateForChart } from 'components/features/utils/util';
import { getDate } from 'components/features/utils/converter';
import { useRouting } from 'hooks/useRouting';
import { useHomeUser, isLoading } from 'hooks/useHomeUser';
import { renderToStaticMarkup } from 'react-dom/server';
import {
  AreaChartLegend,
  LineChartLegend,
  marker,
} from 'components/features/utils/chartUtils';
import * as api from 'config/api';
import {
  DebtAssetAccountHistories,
  DebtAssetAccountLists,
  GetDebtAssetResult,
  updateUser,
} from 'config/api';
import Loading from 'components/commons/loading/Loading';
import { useQuery } from 'react-query';

const noDataColor = '#6366F1';
const colors = [
  '#6366F1',
  '#38BDF8',
  '#F472B6',
  '#22C55E',
  '#F97316',
  '#FACC15',
  '#C026D3',
  '#0891B2',
  '#A16207',
  '#FCA5A5',
];

type AssetStuffingProps = {
  UserBrokerUpdateTime: React.ReactElement;
  groupKey: number;
};

type DebtAsset = {
  debtAsset: GetDebtAssetResult;
};

type AccountHistories = {
  AccountName: string;
  AccountColor: string;
  Histories: {
    Day: Date;
    DebtAmount: number;
  }[];
};

const dataToSeries = (
  data: AccountHistories[]
): Highcharts.SeriesOptionsType[] => {
  return data.map(data => {
    return {
      type: 'area',
      turboThreshold: 15000,
      color: data.AccountColor,
      fillOpacity: 0.6,
      stacking: 'normal',
      name: data.AccountName,
      data: data.Histories.sort(
        (a, b) => a.Day.getTime() - b.Day.getTime()
      ).map(
        (datum): Highcharts.PointOptionsObject => {
          return {
            x: arrangeDateForChart(datum.Day).getTime(),
            y: datum.DebtAmount,
          };
        }
      ),
    };
  }) as SeriesOptionsType[];
};

const PortfolioStuffingChartInternal = ({
  accountHistories,
}: {
  accountHistories: AccountHistories[];
}): React.ReactElement => {
  const series = dataToSeries(accountHistories);
  const [options, setOptions] = useState<Highcharts.Options>({
    exporting: { enabled: false },
    legend: {
      align: 'left',
      enabled: true,
      verticalAlign: 'top',
      useHTML: true,
      itemDistance: -10,
      margin: 5,
      itemStyle: { fontSize: '10px', fontWeight: 'normal', color: '#ffffff' },
      itemHoverStyle: { color: '#cccccc' },
      className: 'override-highcharts-legend',
      labelFormatter: function() {
        if (this instanceof Highcharts.Series) {
          const name = this.name;
          const color = (() => {
            const t = this as unknown;
            if (
              typeof t === 'object' &&
              t &&
              'color' in t &&
              typeof (t as { color: unknown }).color === 'string'
            ) {
              return (t as { color: string }).color;
            }
            return '';
          })();
          if (this.type === 'area') {
            return renderToStaticMarkup(
              <AreaChartLegend color={color} lightOpacity={0.6}>
                {name}
              </AreaChartLegend>
            );
          }
          if (this.type === 'line') {
            return renderToStaticMarkup(
              <LineChartLegend color={color}>{name}</LineChartLegend>
            );
          }
        }
        return '';
      },
    },
    credits: {
      enabled: false,
    },
    rangeSelector: {
      dropdown: 'always',
      x: 8,
      y: 0,
      buttonTheme: {
        fill: 'none',
        stroke: 'none',
        'stroke-width': 0,
        r: 6,
        style: {
          textAlign: 'center',
          color: '#8e9db2',
        },
        states: {
          hover: {
            fill: '#1E293B',
            style: {
              color: 'white',
              fontWeight: 'normal',
            },
          },
          select: {
            fill: '#1E293B',
            style: {
              color: 'white',
              fontWeight: 'normal',
            },
          },
        },
      },
      inputEnabled: false,
      buttons: [
        {
          type: 'week',
          count: 2,
          text: '14日間',
          title: '14日間',
        },
        {
          type: 'month',
          count: 1,
          text: '1ヶ月',
          title: '1ヶ月',
        },
        {
          type: 'month',
          count: 3,
          text: '3ヶ月',
          title: '3ヶ月',
        },
        {
          type: 'month',
          count: 6,
          text: '6ヶ月',
          title: '6ヶ月',
        },
        {
          type: 'year',
          count: 1,
          text: '1年',
          title: '1年',
        },
        {
          type: 'all',
          text: '全期間',
          title: '全期間',
        },
      ],
      selected: 2,
      buttonPosition: { align: 'left' },
    },
    navigator: {
      enabled: false,
    },
    tooltip: {
      borderColor: 'transparent',
      useHTML: true,
      formatter: function(): string {
        const Tooltip = ({
          date,
          children,
        }: {
          date: Date;
          children: React.ReactNodeArray;
        }): React.ReactElement => {
          return (
            <>
              <div
                style={{
                  display: 'grid',
                  gap: '2px',
                  gridAutoRows: 'min-content',
                  gridAutoFlow: 'row',
                }}>
                <div style={{ paddingBottom: '3px', fontSize: '9px' }}>
                  {date.getFullYear()}/{date.getMonth() + 1}/{date.getDate()}
                </div>
                {children}
              </div>
            </>
          );
        };
        const TooltipItem = ({
          name,
          color,
          value,
        }: {
          name: string;
          color: string;
          value: number;
        }): React.ReactElement => {
          return (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'min-content min-content min-content',
                  placeItems: 'center start',
                  gap: '2px',
                }}>
                <div
                  style={{
                    borderRadius: '50%',
                    height: '5px',
                    width: '5px',
                    gridColumn: 1,
                    backgroundColor: color,
                  }}
                />
                <span style={{ fontSize: '10px', gridColumn: 2 }}>{name}:</span>
                <span
                  style={{
                    fontWeight: 'bold',
                    fontSize: '10px',
                    gridColumn: 3,
                  }}>
                  {commaFormat(value / 10000, { numberOfDecimals: 1 })}万円
                </span>
              </div>
            </>
          );
        };
        return renderToStaticMarkup(
          <Tooltip date={new Date(this.x)}>
            {(this.points ?? []).map(point => (
              <TooltipItem
                name={point.series.name}
                color={point.color.toString()}
                value={point.y}
              />
            ))}
          </Tooltip>
        );
      },
    },
    xAxis: {
      ordinal: false,
      type: 'datetime',
      gridLineWidth: 0,
      dateTimeLabelFormats: {
        year: '%Y',
        month: '%Y/%m',
        day: '%m/%d',
        week: '%m/%d',
        hour: '%m/%d',
        minute: '%m/%d',
        second: '%m/%d',
        millisecond: '%m/%d',
      },
      title: { style: { color: '#ffffff' } },
      labels: { style: { color: '#ffffff' } },
      tickColor: '#64748B',
      lineColor: '#64748B',
    },
    yAxis: {
      labels: {
        formatter: function(e): string {
          return typeof this.value === 'number'
            ? commaFormat(this.value / 10000, { numberOfDecimals: 0 })
            : '';
        },
        align: 'left',
        x: 3,
        y: 4,
        style: { color: '#ffffff' },
      },
      title: {
        text: '(万円)',
        align: 'high',
        offset: 0,
        rotation: 0,
        y: 5,
        style: { color: '#ffffff' },
      },
      gridLineColor: '#64748B',
      opposite: true,
    },
    chart: {
      animation: {
        duration: 0,
      },
      backgroundColor: '#475569',
      height: 300,
      marginBottom: 20,
    },
    plotOptions: {
      series: {
        ...marker,
        turboThreshold: 15000,
      },
      area: {
        events: {
          legendItemClick: (): false => false,
        },
        turboThreshold: 15000,
      },
      line: {
        events: {
          legendItemClick: (): false => false,
        },
        turboThreshold: 15000,
      },
    },
    series,
  });

  useEffect(() => {
    setOptions(prev => {
      return merge({ ...prev, series });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountHistories]);

  const ref = useRef<HighchartsReact.RefObject>(null);
  return (
    <>
      <HighchartsReact
        highcharts={Highcharts}
        constructorType="stockChart"
        options={options}
        ref={ref}
      />
    </>
  );
};

const NoDebit = () => {
  return (
    <div className="flex justify-center py-12 pl-3 bg-bluegray-600">
      <img className="" src="/static/images/no_debt.svg" alt="" />
    </div>
  );
};

const DebitLoading = () => {
  return (
    <>
      <div className="bg-bluegray-600">
        <Loading
          loading={true}
          circularLoading={true}
          style={{
            paddingTop: '140px',
            paddingBottom: '140px',
          }}
        />
      </div>
      <div className="p-4">
        <div className="flex flex-row mt-8 mb-4">
          <div className="text-sm font-bold">負債総額</div>
        </div>
        <Loading
          loading={true}
          circularLoading={true}
          style={{
            paddingTop: '60px',
            paddingBottom: '60px',
          }}
        />
      </div>
    </>
  );
};

const AssetStuffingDebtList = ({
  UserBrokerUpdateTime,
  groupKey,
  debtAsset,
  isFetching,
  isRefetching,
}: AssetStuffingProps & DebtAsset & DataLoadingProps) => {
  const { pushRouting } = useRouting();
  const { homeUser, getHomeUser } = useHomeUser(200);
  const [isHiddenAssetD, setIsHiddenAssetD] = useState<boolean | null>(null);

  const accountLists = debtAsset.TotalAsset.AccountLists;
  const accountHistories = debtAsset.TotalAsset.AccountHistories;
  const groupAssets = debtAsset.GroupAssets;

  const sortedAccountLists = accountLists.sort((a, b) => {
    return a.RealDebtAmount > b.RealDebtAmount ? 1 : -1;
  });
  const colorList = sortedAccountLists.map((list, i) => {
    const num = Number(i.toString().slice(-1));
    const color = colors[num] !== undefined ? colors[num] : noDataColor;
    return {
      account: list.Account,
      color: color,
    };
  });

  const getAccountColor = (acount: string) => {
    const list = colorList.find(list => list.account === acount);
    return list !== undefined ? list.color : noDataColor;
  };

  const getGroupAssetsAccountLists = (key: number) => {
    const list = groupAssets.find(a => Number(a.ID) === key)
      ?.AccountLists as DebtAssetAccountLists[];
    return list !== undefined ? list : null;
  };

  const dispAccountLists = accountLists.filter(
    list => list.RealDebtAmount !== 0
  );

  const debtAssetsList: DebtAssetAccountLists[] | null =
    groupKey === 0 ? dispAccountLists : getGroupAssetsAccountLists(groupKey);

  useEffect(() => {
    getHomeUser();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (homeUser.tag === 'LOADED') {
      const value = homeUser.isHiddenAssetD.value;
      setIsHiddenAssetD(value);
    }
  }, [homeUser]);

  if (isLoading(homeUser)) return <DebitLoading />;

  if (debtAssetsList === null || debtAssetsList.length === 0)
    return <NoDebit />;

  const sortedDebtAssetsList = debtAssetsList.sort((a, b) => {
    return a.RealDebtAmount > b.RealDebtAmount ? 1 : -1;
  });

  const getGroupAssetsAccountHistories = (key: number) => {
    const list = groupAssets.find(list => Number(list.ID) === key)
      ?.AccountHistories;
    return list !== undefined ? list : null;
  };

  const debtAssetsHistories: DebtAssetAccountHistories[] | null =
    groupKey === 0
      ? accountHistories
      : getGroupAssetsAccountHistories(groupKey);

  if (debtAssetsHistories === null) return <></>;

  const dispDebtAssetsHistories = debtAssetsHistories
    .map(list => {
      const item = list.Histories.map(histories => {
        return histories.DebtAmount !== 0 ? histories : null;
      }).filter((item): item is NonNullable<typeof item> => item != null);

      return {
        Account: list.Account,
        AccountName: list.AccountName,
        Histories: item,
      };
    })
    .filter((item): item is NonNullable<typeof item> => item != null);

  const chartDebtAssetsHistories: AccountHistories[] = dispDebtAssetsHistories.map(
    list => {
      return {
        AccountName: list.AccountName,
        AccountColor: getAccountColor(list.Account),
        Histories: list.Histories.map(item => {
          return {
            Day: getDate(item.Day),
            DebtAmount: item.DebtAmount,
          };
        }),
      };
    }
  );

  const realDebtAmountList = sortedDebtAssetsList.map(list => {
    return list.RealDebtAmount;
  });
  const debtAllPrice = realDebtAmountList.reduce(
    (previousValue, currentValue) => {
      return previousValue + currentValue;
    }
  );

  const updateHideDebt = async () => {
    setIsHiddenAssetD(!isHiddenAssetD);
    await updateUser({
      HideAssetD: !isHiddenAssetD,
    });
  };

  return (
    <>
      {accountLists.length === 0 ||
      accountHistories.length === 0 ||
      groupAssets.length === 0 ? (
        <NoDebit />
      ) : (
        <>
          <div className="pt-4 bg-bluegray-600">
            {isFetching && isRefetching ? (
              <>
                <Loading
                  loading={true}
                  circularLoading={true}
                  style={{
                    paddingTop: '130px',
                    paddingBottom: '130px',
                  }}
                />
              </>
            ) : (
              <PortfolioStuffingChartInternal
                {...{ accountHistories: chartDebtAssetsHistories }}
              />
            )}
          </div>
          <div className="flex flex-row items-center relative z-10 -mt-6 mb-4 bg-bluegray-600">
            {UserBrokerUpdateTime}
          </div>
          <div className="p-4">
            <div className="flex flex-row items-center mt-2 mb-4">
              <div className="text-sm font-bold">負債総額</div>
              <div className="ml-auto text-2xl font-bold">
                {isHiddenAssetD ? '---' : commaFormat(debtAllPrice)}
                <span className="ml-1 text-xs">円</span>
              </div>
              <div
                className={`icon-eye_${
                  isHiddenAssetD ? 'close' : 'open'
                }_img ml-2 text-bluegray-400 cursor-pointer`}
                onClick={() => updateHideDebt()}></div>
            </div>

            {sortedDebtAssetsList.map((list, key) => {
              const color = getAccountColor(list.Account);
              return (
                <div key={key}>
                  <div
                    className="border rounded-lg px-4 py-6 mb-2 cursor-pointer"
                    onClick={(): void =>
                      pushRouting(
                        { path: `/accountdetail/${list.Account}` },
                        true
                      )
                    }>
                    <div
                      className="grid"
                      style={{ gridTemplateColumns: 'auto 2fr 1fr auto' }}>
                      <div
                        className="flex w-4 h-4 rounded-full mt-1 mr-2"
                        style={{ backgroundColor: color }}></div>
                      <div className="flex flex-col items-start">
                        <div className="font-bold">{list.AccountName}</div>
                      </div>
                      <div className="flex items-center ml-auto">
                        {commaFormat(list.RealDebtAmount)}
                        <span className="text-xs">円</span>
                      </div>
                      <div className="flex items-center ml-4 w-2">
                        <img
                          src="/static/images/icon/icon-arrow-right.svg"
                          alt=""
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </>
  );
};

export const PortfolioStuffingDebt = ({
  UserBrokerUpdateTime,
  groupKey,
}: AssetStuffingProps): React.ReactElement => {
  const { data: debtAssetData, isFetching, isRefetching, refetch } = useQuery(
    'portfolioStuffingDebtAsset',
    fetchDebtAsset,
    {
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    (async () => {
      await refetch();
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupKey]);

  if (debtAssetData === null || debtAssetData === undefined)
    return <DebitLoading />;

  return (
    <AssetStuffingDebtList
      {...{
        UserBrokerUpdateTime,
        groupKey,
        debtAsset: debtAssetData as GetDebtAssetResult,
        isFetching,
        isRefetching,
      }}
    />
  );
};

const fetchDebtAsset = async () => {
  return await api.getDebtAsset();
};
